/**
 * PhotoLightbox Component
 *
 * A fullscreen lightbox for viewing route photos with swipe navigation.
 * Features:
 * - Swipe left/right to navigate between photos
 * - Pinch to zoom on touch devices
 * - Tap outside photo to close
 * - Semi-transparent backdrop
 */

import { useState, useRef, useEffect, type CSSProperties, type TouchEvent } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface PhotoLightboxProps {
  /** Array of photo URLs to display */
  photos: Array<{ url: string; thumbnailUrl?: string }>;
  /** Index of the initially selected photo */
  initialIndex: number;
  /** Called when lightbox is closed */
  onClose: () => void;
}

export const PhotoLightbox = ({ photos, initialIndex, onClose }: PhotoLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isVisible, setIsVisible] = useState(false);
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const initialPinchDistance = useRef(0);
  const initialScale = useRef(1);
  const isDragging = useRef(false);

  const currentPhoto = photos[currentIndex];

  // Animate in on mount
  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timeout);
  }, []);

  // Handle close with animation
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  // Navigate to previous photo
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setScale(1);
      setTranslateX(0);
    }
  };

  // Navigate to next photo
  const goToNext = () => {
    if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setScale(1);
      setTranslateX(0);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  // Touch handling for swipe
  const handleTouchStart = (e: TouchEvent) => {
    const touch0 = e.touches[0];
    const touch1 = e.touches[1];

    if (e.touches.length === 1 && touch0) {
      // Single touch - prepare for swipe
      touchStartX.current = touch0.clientX;
      touchStartY.current = touch0.clientY;
      isDragging.current = true;
    } else if (e.touches.length === 2 && touch0 && touch1) {
      // Pinch zoom
      const distance = Math.hypot(touch0.clientX - touch1.clientX, touch0.clientY - touch1.clientY);
      initialPinchDistance.current = distance;
      initialScale.current = scale;
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    const touch0 = e.touches[0];
    const touch1 = e.touches[1];

    if (e.touches.length === 1 && isDragging.current && scale === 1 && touch0) {
      // Swipe gesture (only when not zoomed)
      const deltaX = touch0.clientX - touchStartX.current;
      setTranslateX(deltaX);
    } else if (e.touches.length === 2 && touch0 && touch1) {
      // Pinch zoom
      const distance = Math.hypot(touch0.clientX - touch1.clientX, touch0.clientY - touch1.clientY);
      const newScale = Math.min(
        3,
        Math.max(1, initialScale.current * (distance / initialPinchDistance.current))
      );
      setScale(newScale);
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;

    // Check for swipe threshold
    if (Math.abs(translateX) > 80 && scale === 1) {
      if (translateX > 0 && currentIndex > 0) {
        goToPrevious();
      } else if (translateX < 0 && currentIndex < photos.length - 1) {
        goToNext();
      }
    }

    setTranslateX(0);

    // Reset scale if it's close to 1
    if (scale < 1.1) {
      setScale(1);
    }
  };

  // Double tap to zoom
  const lastTap = useRef(0);
  const handleImageClick = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      // Double tap - toggle zoom
      setScale(scale > 1 ? 1 : 2);
    }
    lastTap.current = now;
  };

  return (
    <div
      style={{
        ...backdropStyle,
        opacity: isVisible ? 1 : 0,
      }}
      onClick={handleClose}
    >
      {/* Close button */}
      <button style={closeButtonStyle} onClick={handleClose} aria-label="Close photo viewer">
        <CloseIcon />
      </button>

      {/* Photo counter */}
      {photos.length > 1 && (
        <div style={counterStyle}>
          {currentIndex + 1} / {photos.length}
        </div>
      )}

      {/* Navigation arrows (desktop) */}
      {photos.length > 1 && currentIndex > 0 && (
        <button
          style={{ ...navButtonStyle, left: 16 }}
          onClick={(e) => {
            e.stopPropagation();
            goToPrevious();
          }}
          aria-label="Previous photo"
        >
          <ChevronLeftIcon fontSize="large" />
        </button>
      )}

      {photos.length > 1 && currentIndex < photos.length - 1 && (
        <button
          style={{ ...navButtonStyle, right: 16 }}
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          aria-label="Next photo"
        >
          <ChevronRightIcon fontSize="large" />
        </button>
      )}

      {/* Photo container */}
      <div
        style={photoContainerStyle}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentPhoto?.url}
          alt={`Photo ${currentIndex + 1}`}
          style={{
            ...photoStyle,
            transform: `translateX(${translateX}px) scale(${scale})`,
            transition: isDragging.current ? 'none' : 'transform 200ms ease-out',
          }}
          onClick={handleImageClick}
          draggable={false}
        />
      </div>
    </div>
  );
};

// Styles
const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  transition: 'opacity 200ms ease-out',
};

const closeButtonStyle: CSSProperties = {
  position: 'absolute',
  top: 16,
  right: 16,
  width: 44,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  cursor: 'pointer',
  zIndex: 10,
};

const counterStyle: CSSProperties = {
  position: 'absolute',
  top: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: 500,
  padding: '4px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: '12px',
};

const navButtonStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 48,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  cursor: 'pointer',
  zIndex: 10,
};

const photoContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  padding: '60px 20px',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const photoStyle: CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

export default PhotoLightbox;
